import { Controller } from "stimulus"

// Drives the Ask-Us pill's lazy frame while the personalized question is still
// generating. Turbo loads the frame once; this controller reloads it on a
// backoff until the endpoint reports the question is ready (data-nudge-ready),
// then stops. Modeled on frame_retry_controller; attach to the <turbo-frame>
// so it survives each child replacement.
//
// Three rotation attempts can each use three validation attempts, with a 120s
// client timeout per request. Keep polling long enough for that bounded budget.
// Backoff caps the request rate while the previous question remains clickable.
//
// The loop advances on turbo:frame-load (successful reload),
// turbo:fetch-request-error (network-level failure) and turbo:frame-missing (a
// 2xx/5xx response without the frame: a server error page, or an auth redirect
// to login on session expiry) — a failed poll must keep retrying, not die on the
// fallback, and frame-missing must be suppressed so Turbo does not navigate the
// whole tab to that response. connect() also kicks the loop when the frame is
// already settled but not ready: a turbo-cache back-navigation restores a
// [complete] frame and fires no frame-load, and a fast first load can beat this
// connect().
const INITIAL_DELAY = 1500
const MAX_DELAY = 15000
const BACKOFF_FACTOR = 2
const MAX_ELAPSED = 1080000

export default class extends Controller {
  initialize() {
    this.tick = this.tick.bind(this)
    this.onMissing = this.onMissing.bind(this)
  }

  connect() {
    this.startedAt = Date.now()
    this.delay = INITIAL_DELAY
    this.element.addEventListener("turbo:frame-load", this.tick)
    this.element.addEventListener("turbo:fetch-request-error", this.tick)
    this.element.addEventListener("turbo:frame-missing", this.onMissing)
    if (!this.ready) this.scheduleReload()
  }

  disconnect() {
    this.clearTimer()
    this.element.removeEventListener("turbo:frame-load", this.tick)
    this.element.removeEventListener("turbo:fetch-request-error", this.tick)
    this.element.removeEventListener("turbo:frame-missing", this.onMissing)
  }

  // A poll response with no matching frame (error page or login redirect) would,
  // by default, make Turbo visit that response and yank the tab off the page.
  // Suppress that and treat it as a failed attempt so the loop keeps retrying.
  onMissing(event) {
    event.preventDefault()
    this.tick()
  }

  // Runs after each successful load (children replaced) and each failed fetch
  // (children unchanged, still not ready): stop when ready, past the elapsed
  // budget, or hidden; otherwise schedule the next reload.
  tick() {
    if (this.ready || this.exhausted || !this.visible) return this.stop()

    this.scheduleReload()
  }

  scheduleReload() {
    this.clearTimer()
    this.timer = setTimeout(() => this.reload(), this.delay)
    this.delay = Math.min(this.delay * BACKOFF_FACTOR, MAX_DELAY)
  }

  reload() {
    if (!this.visible) return this.stop()

    this.element.reload()
  }

  stop() {
    this.clearTimer()
  }

  clearTimer() {
    if (!this.timer) return

    clearTimeout(this.timer)
    this.timer = null
  }

  get exhausted() {
    return Date.now() - this.startedAt >= MAX_ELAPSED
  }

  get ready() {
    return this.element.querySelector('[data-nudge-ready="true"]') !== null
  }

  // getClientRects is empty for a display:none element and works on old WebViews
  // where checkVisibility is unavailable.
  get visible() {
    return this.element.getClientRects().length > 0
  }
}
